import { FlowModel } from "../models/FlowModel";
import ApiService from "./ApiService";
import request from "../shared/http";
import appConfig from "../shared/js/app-config";

export class FlowModelService {
  constructor() {
    this.model = new FlowModel();
  }

  // Node operations
  createNode(type, position, data) {
    const node = {
      id: this.model.generateNodeId(),
      type,
      position,
      data,
      isUpdated: false,
    };

    if (this.model.isValidNode(node)) {
      this.model.addNode(node);
      return node;
    }

    throw new Error("Invalid node data");
  }

  updateNode(id, data) {
    const node = this.model.getNode(id);
    if (node) {
      this.model.updateNode(id, data);
    } else {
      throw new Error(`Node with id ${id} not found`);
    }
  }

  deleteNode(id) {
    console.log("FlowService deleteNode called with id:", id);
    const node = this.model.getNode(id);
    if (node) {
      console.log("Node found, deleting from model...");
      this.model.deleteNode(id);
      console.log("Node deleted from model");
    } else {
      console.log("Node not found in model");
      throw new Error(`Node with id ${id} not found`);
    }
  }

  getNode(id) {
    return this.model.getNode(id);
  }

  getAllNodes() {
    return this.model.getAllNodes();
  }

  // Edge operations
  createEdge(sourceId, targetId, sourceHandle, targetHandle) {
    console.log("=== FlowService createEdge - INPUT ===", {
      sourceId,
      targetId,
      sourceHandle,
      targetHandle,
      sourceIdType: typeof sourceId,
      targetIdType: typeof targetId,
    });

    // Extract actual node IDs from handle IDs if they contain handle information
    const extractNodeId = (handleId) => {
      console.log("=== Handle ID Parsing Input ===", {
        handleId,
        includesLabel: handleId.includes("-label-"),
        includesMain: handleId.includes("-main"),
      });

      if (handleId.includes("-label-")) {
        const parts = handleId.split("-label-");
        console.log("=== Handle ID Parsing - Label Handle ===", {
          handleId,
          parts,
          extractedNodeId: parts[0],
        });
        return parts[0];
      }

      if (handleId.includes("-main")) {
        const parts = handleId.split("-main");
        console.log("=== Handle ID Parsing - Main Handle ===", {
          handleId,
          parts,
          extractedNodeId: parts[0],
        });
        return parts[0];
      }

      console.log("=== Handle ID Parsing - Regular Node ID ===", {
        handleId,
        extractedNodeId: handleId,
      });
      return handleId;
    };

    const actualSourceId = extractNodeId(sourceId);
    const actualTargetId = extractNodeId(targetId);

    console.log("=== FlowService createEdge - PROCESSED ===", {
      originalSourceId: sourceId,
      originalTargetId: targetId,
      actualSourceId: actualSourceId,
      actualTargetId: actualTargetId,
      sourceHandle,
      targetHandle,
      sourceHasLabel: sourceId.includes("-label-"),
      targetHasLabel: targetId.includes("-label-"),
    });

    // Check if nodes exist
    if (
      !this.model.getNode(actualSourceId) ||
      !this.model.getNode(actualTargetId)
    ) {
      console.log(
        "=== FlowService createEdge - CONNECTION FAILED: Nodes not found ===",
        {
          actualSourceId,
          actualTargetId,
          availableNodes: this.model.getAllNodes().map((n) => n.id),
        }
      );
      throw new Error("Cannot create connection between these nodes");
    }

    // Prevent self-connection
    if (actualSourceId === actualTargetId) {
      console.log(
        "=== FlowService createEdge - CONNECTION FAILED: Self-connection ==="
      );
      throw new Error("Cannot connect node to itself");
    }

    // Check if this specific handle connection already exists
    const existingEdge = this.model
      .getAllEdges()
      .find(
        (edge) =>
          edge.source === actualSourceId &&
          edge.target === actualTargetId &&
          edge.sourceHandle === sourceHandle &&
          edge.targetHandle === targetHandle
      );

    if (existingEdge) {
      console.log(
        "=== FlowService createEdge - CONNECTION FAILED: Duplicate handle connection ===",
        {
          existingEdge: existingEdge.id,
          sourceHandle,
          targetHandle,
        }
      );
      throw new Error("Connection already exists between these handles");
    }

    // Each output handle can drive only one next node. Routers/lists/buttons already expose
    // separate handles per branch, so duplicate handle connections are ambiguous at runtime.
    const existingSourceHandleConnection = this.model
      .getAllEdges()
      .find((edge) =>
        edge.source === actualSourceId &&
        String(edge.sourceHandle || "") === String(sourceHandle || "")
      );

    if (existingSourceHandleConnection) {
      console.log(
        "=== FlowService createEdge - CONNECTION FAILED: Source handle already connected ===",
        {
          sourceHandle,
          existingConnection: existingSourceHandleConnection.id,
          existingTarget: existingSourceHandleConnection.target,
        }
      );
      throw new Error(
        "This output is already connected. Remove the existing line before connecting another node."
      );
    }

    const edge = {
      id: this.model.generateEdgeId(),
      source: actualSourceId,
      target: actualTargetId,
      sourceHandle: sourceHandle,
      targetHandle: targetHandle,
    };

    if (this.model.isValidEdge(edge)) {
      this.model.addEdge(edge);
      console.log("=== FlowService createEdge - SUCCESS ===", {
        edgeId: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
      });
      return edge;
    }

    throw new Error("Invalid edge data");
  }

  deleteEdge(id) {
    console.log("=== FlowService deleteEdge ===", {
      edgeId: id,
      edgesBefore: this.model.getAllEdges().length,
    });
    this.model.deleteEdge(id);
    console.log("=== FlowService deleteEdge - COMPLETED ===", {
      edgeId: id,
      edgesAfter: this.model.getAllEdges().length,
    });
  }

  getAllEdges() {
    return this.model.getAllEdges();
  }

  // State operations
  getState() {
    return this.model.getState();
  }

  migrateLegacyVisualSearchNode(node) {
    if (!node || node.type !== "visualProductSearch") return node;
    const prompt = node.data?.body || node.data?.text
      || "Please send a clear photo of the product you are looking for.";
    const {
      onChange, onDelete, sessionVariable, body, ...rest
    } = node.data || {};
    return {
      ...node,
      type: "normalMessage",
      data: {
        text: prompt,
        ...rest,
      },
    };
  }

  setState(state) {
    const nodes = (state?.nodes || []).map((n) => this.migrateLegacyVisualSearchNode(n));
    this.model.setState({ ...state, nodes });
  }

  // Event handling
  handleNodeChange(event) {
    const { id, field, value } = event;
    console.log("=== SERVICE NODE CHANGE ===", { id, field, value });
    const node = this.model.getNode(id);

    if (node) {
      // Preserve existing handlers when updating data
      const existingHandlers = {};
      const handlerFields = [
        "onChangeBody",
        "onChangeMediaType",
        "onChangeCaption",
        "onChangeFileName",
        "onChangeFilePath",
        "onChangeFile",
        "onChangePreviewUrl",
        "onDelete",
        "onChangeText",
        "onChangeProduct",
        "onChangeTemplate",
        "onChangeVariables",
        "onChangeHeader",
        "onChangeFooter",
        "onChangePaymentType",
        "onChangePaymentItems",
        "onChangeConfigurationId",
        "onChangeConfigurationName",
        "onChangeMethod",
        "onChangeUrl",
        "onChangeHeadersEnabled",
        "onChangeHeaders",
        "onChangeBodyEnabled",
        "onChangeRequestBody",
        "onChangeTestEnabled",
        "onChangeTestVariables",
        "onChangeTestStatusCode",
        "onChangeTestResponseBody",
        "onChangeSaveResponseAsVariables",
        "onChangeResponseRoutingEnabled",
        "onChangeExpectedStatuses",
        "onChangeVerticalAction",
      ];

      handlerFields.forEach((handlerField) => {
        if (node.data[handlerField]) {
          existingHandlers[handlerField] = node.data[handlerField];
        }
      });

      const updatedData = {
        ...node.data,
        ...existingHandlers, // Preserve handlers
        [field]: value,
      };
      console.log("=== UPDATING NODE DATA ===", {
        id,
        oldData: node.data,
        newData: updatedData,
        preservedHandlers: Object.keys(existingHandlers),
      });
      this.model.updateNode(id, updatedData);
    }
  }
  createCustomNode(position) {
    return this.createNode("custom", position, {
      title: "",
      description: "",
    });
  }

  createButtonNode(position) {
    return this.createNode("buttonNode", position, {
      buttonLabel: "Click me",
    });
  }

  createNormalMessageNode(position) {
    return this.createNode("normalMessage", position, {
      text: "",
      onChangeText: (id, value) => {
        this.handleNodeChange({ id, field: "text", value });
      },
    });
  }
   createCatalogMessageNode(position) {
    return this.createNode("catalogMessage", position, {
      text: "",
      onChangeText: (id, value) => {
        this.handleNodeChange({ id, field: "text", value });
      },
    });

  }

  createTemplateMessageNode(position) {
    return this.createNode("templateMessage", position, {
      templateId: null,
      variables: { header: {}, body: {}, footer: {} },
      onChangeTemplate: (id, templateId) => {
        this.handleNodeChange({ id, field: "templateId", value: templateId });
      },
      onChangeVariables: (id, variables) => {
        this.handleNodeChange({ id, field: "variables", value: variables });
      },
      onDelete: (id) => {
        this.deleteNode(id);
      },
    });
  }

  createInteractiveMessageNode(position) {
    return this.createNode("interactiveMessage", position, {
      header: "Header",
      body: "Body",
      footer: "Powered by Orkaapps",
      labels: ["Label 1", "Label 2", "Label 3"],
      sections: [],
      listHeader: "",
      messageType: "buttons",
      answerVariable: "",
      onDelete: (id) => {
        console.log("Delete requested for node:", id);
      },
      onChangeHeader: (id, value) => {
        this.handleNodeChange({ id, field: "header", value });
      },
      onChangeBody: (id, value) => {
        this.handleNodeChange({ id, field: "body", value });
      },
      onChangeFooter: (id, value) => {
        this.handleNodeChange({ id, field: "footer", value });
      },
      onChangeSections: (id, value) => {
        this.handleNodeChange({ id, field: "sections", value });
      },
      onChangeLabels: (id, value) => {
        this.handleNodeChange({ id, field: "labels", value });
      },
      onChangeMessageType: (id, value) => {
        this.handleNodeChange({ id, field: "messageType", value });
      },
      onChangeListHeader: (id, value) => {
        this.handleNodeChange({ id, field: "listHeader", value });
      },
      onChangeAnswerVariable: (id, value) => {
        this.handleNodeChange({ id, field: "answerVariable", value });
      },
    });
  }

  createQuestionMessageNode(position) {
    return this.createNode("questionMessage", position, {
      body: "",
      labels: [],
      answerVariable: "",
      answerFormat: "any",
      maxAttempts: 3,
      validationErrorMessage: "",
      regexPattern: "",
      abandonMessage: "",
      onChangeBody: (id, value) => {
        this.handleNodeChange({ id, field: "body", value });
      },
      onChangeLabels: (id, value) => {
        this.handleNodeChange({ id, field: "labels", value });
      },
      onChangeAnswerVariable: (id, value) => {
        this.handleNodeChange({ id, field: "answerVariable", value });
      },
      onDelete: (id) => {
        this.deleteNode(id);
      },
    });
  }

  createWebhookMessageNode(position) {
    return this.createNode("webhookMessage", position, {
      method: "POST",
      url: "",
      headersEnabled: false,
      headers: [],
      bodyEnabled: false,
      requestBody: "",
      testEnabled: false,
      testVariables: [],
      testStatusCode: "",
      testResponseBody: "",
      saveResponseAsVariables: false,
      responseRoutingEnabled: false,
      expectedStatuses: ["200"],
      onChangeMethod: (id, value) => {
        this.handleNodeChange({ id, field: "method", value: "POST" });
      },
      onChangeUrl: (id, value) => {
        this.handleNodeChange({ id, field: "url", value });
      },
      onChangeHeadersEnabled: (id, value) => {
        this.handleNodeChange({ id, field: "headersEnabled", value });
      },
      onChangeHeaders: (id, value) => {
        this.handleNodeChange({ id, field: "headers", value });
      },
      onChangeBodyEnabled: (id, value) => {
        this.handleNodeChange({ id, field: "bodyEnabled", value });
      },
      onChangeRequestBody: (id, value) => {
        this.handleNodeChange({ id, field: "requestBody", value });
      },
      onChangeTestEnabled: (id, value) => {
        this.handleNodeChange({ id, field: "testEnabled", value });
      },
      onChangeTestVariables: (id, value) => {
        this.handleNodeChange({ id, field: "testVariables", value });
      },
      onChangeTestStatusCode: (id, value) => {
        this.handleNodeChange({ id, field: "testStatusCode", value });
      },
      onChangeTestResponseBody: (id, value) => {
        this.handleNodeChange({ id, field: "testResponseBody", value });
      },
      onChangeSaveResponseAsVariables: (id, value) => {
        this.handleNodeChange({ id, field: "saveResponseAsVariables", value });
      },
      onChangeResponseRoutingEnabled: (id, value) => {
        this.handleNodeChange({ id, field: "responseRoutingEnabled", value });
      },
      onChangeExpectedStatuses: (id, value) => {
        this.handleNodeChange({ id, field: "expectedStatuses", value });
      },
      onDelete: (id) => {
        this.deleteNode(id);
      },
    });
  }

  createFlowMessageNode(position) {
    return this.createNode("flowMessage", position, {
      body: "",
      buttonLabel: "Open Form",
      flowId: "",
      flowName: "",
      answerVariable: "",
      handlerType: "flow",
      onChangeBody: (id, value) => {
        this.handleNodeChange({ id, field: "body", value });
      },
      onChangeButtonLabel: (id, value) => {
        this.handleNodeChange({ id, field: "buttonLabel", value });
      },
      onChangeFlowId: (id, value) => {
        this.handleNodeChange({ id, field: "flowId", value });
      },
      onChangeFlowName: (id, value) => {
        this.handleNodeChange({ id, field: "flowName", value });
      },
      onChangeAnswerVariable: (id, value) => {
        this.handleNodeChange({ id, field: "answerVariable", value });
      },
      onChangeHandlerType: (id, value) => {
        this.handleNodeChange({ id, field: "handlerType", value });
      },
      onDelete: (id) => {
        this.deleteNode(id);
      },
    });
  }

  createConditionNode(position) {
    return this.createNode("conditionNode", position, {
      routerMode: "message",
      actionProfile: "customer_message",
      logic: "first_match",
      defaultLabel: "Default / fallback",
      branches: [
        {
          id: "has_reply",
          label: "Customer replied",
          description: "Customer sent any message.",
          match: "all",
          conditions: [
            {
              source: "message",
              field: "text",
              operator: "exists",
              value: "",
            },
          ],
        },
        {
          id: "media_uploaded",
          label: "Media uploaded",
          description: "Customer uploaded an image, document, audio, or video.",
          match: "all",
          conditions: [
            {
              source: "message",
              field: "has_media",
              operator: "equals",
              value: "true",
            },
          ],
        },
      ],
      conditions: [],
      onChangeRouter: (id, value) => {
        this.handleNodeChange({ id, field: "routerMode", value: value.routerMode });
        this.handleNodeChange({ id, field: "actionProfile", value: value.actionProfile });
        this.handleNodeChange({ id, field: "logic", value: value.logic });
        this.handleNodeChange({ id, field: "defaultLabel", value: value.defaultLabel });
        this.handleNodeChange({ id, field: "branches", value: value.branches });
      },
      onChangeConditions: (id, value) => {
        this.handleNodeChange({ id, field: "conditions", value });
      },
      onDelete: (id) => {
        this.deleteNode(id);
      },
    });
  }

  createVerticalActionNode(position) {
    return this.createNode("verticalActionNode", position, {
      actionId: "ticket.hold.create",
      title: "Reserve ticket",
      ticketEventId: "",
      ticketPackageEventId: "",
      travelBooking: true,
      hotelVariable: "",
      vehicleVariable: "",
      ticketTierId: "",
      qty: "",
      seatVariable: "",
      passengerVariable: "",
      appointmentTypeId: "",
      appointmentResourceId: "",
      appointmentStartVariable: "",
      onChangeVerticalAction: (id, value) => {
        Object.entries(value || {}).forEach(([field, fieldValue]) => {
          this.handleNodeChange({ id, field, value: fieldValue });
        });
      },
      onDelete: (id) => {
        this.deleteNode(id);
      },
    });
  }

  createPaymentMessageNode(position) {
    return this.createNode("paymentMessage", position, {
      paymentType: "order_payment",
      header: "",
      body: "",
      footer: "",
      paymentItems: [{ name: "", amount: "" }],
      configurationId: "",
      configurationName: "",
      onChangePaymentType: (id, value) => {
        this.handleNodeChange({ id, field: "paymentType", value });
      },
      onChangeHeader: (id, value) => {
        this.handleNodeChange({ id, field: "header", value });
      },
      onChangeBody: (id, value) => {
        this.handleNodeChange({ id, field: "body", value });
      },
      onChangeFooter: (id, value) => {
        this.handleNodeChange({ id, field: "footer", value });
      },
      onChangePaymentItems: (id, value) => {
        this.handleNodeChange({ id, field: "paymentItems", value });
      },
      onChangeConfigurationId: (id, value) => {
        this.handleNodeChange({ id, field: "configurationId", value });
      },
      onChangeConfigurationName: (id, value) => {
        this.handleNodeChange({ id, field: "configurationName", value });
      },
      onDelete: (id) => {
        this.deleteNode(id);
      },
    });
  }

  createMediaMessageNode(position) {
    return this.createNode("mediaMessage", position, {
      mediaType: "image",
      caption: "",
      fileName: "",
      filePath: "",
      customFileName: "",
      selectedFile: null,
      previewUrl: "",
      mediaId: "",
      onChangeMediaType: (id, value) => {
        this.handleNodeChange({ id, field: "mediaType", value });
      },
      onChangeCaption: (id, value) => {
        this.handleNodeChange({ id, field: "caption", value });
      },
      onChangeFileName: (id, value) => {
        this.handleNodeChange({ id, field: "fileName", value });
      },
      onChangeFilePath: (id, value) => {
        this.handleNodeChange({ id, field: "filePath", value });
      },
      onChangeFile: (id, value) => {
        this.handleNodeChange({ id, field: "selectedFile", value });
      },
      onChangePreviewUrl: (id, value) => {
        this.handleNodeChange({ id, field: "previewUrl", value });
      },
      onDelete: (id) => {
        this.deleteNode(id);
      },
    });
  }

  createLabelChildNode(position) {
    return this.createNode("labelChildNode", position, {
      labels: ["Label 1", "Label 2", "Label 3"],
    });
  }

  createRowNode(position, data = {}) {
    return this.createNode("rowNode", position, {
      title: data.title || "Row Title",
      description: data.description || "No description",
      sectionIndex: data.sectionIndex || 0,
      rowIndex: data.rowIndex || 0,
      parentNodeId: data.parentNodeId || null,
      onDeleteRow: data.onDeleteRow || null,
      edges: data.edges || [],
    });
  }
  validateNodeData(nodeData, nodeType) {
    switch (nodeType) {
      case "custom":
        return (
          nodeData.title !== undefined && nodeData.description !== undefined
        );
      case "buttonNode":
        return nodeData.buttonLabel !== undefined;
      case "normalMessage":
        return nodeData.text !== undefined;
      case "interactiveMessage":
        return (
          nodeData.header !== undefined &&
          nodeData.body !== undefined &&
          nodeData.footer !== undefined &&
          Array.isArray(nodeData.labels) &&
          Array.isArray(nodeData.sections) &&
          (nodeData.messageType === "buttons" ||
            nodeData.messageType === "list")
        );
      case "questionMessage":
        return nodeData.body !== undefined && Array.isArray(nodeData.labels);
      case "flowMessage":
        return nodeData.body !== undefined && nodeData.buttonLabel !== undefined;
      case "conditionNode":
        return Array.isArray(nodeData.branches) || Array.isArray(nodeData.conditions);
      case "verticalActionNode":
        return nodeData.actionId !== undefined;
      case "webhookMessage":
        return nodeData.method !== undefined && nodeData.url !== undefined;
      case "paymentMessage":
        return (
          nodeData.paymentType !== undefined
          && nodeData.header !== undefined
          && nodeData.body !== undefined
          && nodeData.footer !== undefined
          && nodeData.paymentItems !== undefined
          && (
            nodeData.paymentType !== "order_payment"
            || nodeData.configurationId !== undefined
          )
        );
      case "mediaMessage":
        return (
          nodeData.mediaType !== undefined &&
          nodeData.caption !== undefined &&
          nodeData.filePath !== undefined
        );
      case "labelChildNode":
        return Array.isArray(nodeData.labels);
      case "rowNode":
        return (
          nodeData.title !== undefined &&
          nodeData.description !== undefined &&
          nodeData.parentNodeId !== undefined
        );
      default:
        return true;
    }
  }
  // Helper function to transform labels array for interactive_message nodes
  transformLabelsForExport(node) {
    if (
      (node.type === "interactiveMessage" || node.type === "questionMessage") &&
      node.data &&
      node.data.labels &&
      Array.isArray(node.data.labels) &&
      node.data.labels.length > 0
    ) {
      // Check if labels are already in the new format (objects with label_id)
      const isNewFormat = node.data.labels.every(
        (label) => typeof label === "object" && label.label && label.label_id
      );

      if (!isNewFormat) {
        // Transform string array to object array
        const transformedLabels = node.data.labels.map((label, index) => ({
          label: label,
          label_id: `btn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${index}`,
        }));

        return {
          ...node,
          data: {
            ...node.data,
            labels: transformedLabels,
          },
        };
      }
    }
    return node;
  }

  // Helper function to transform sections for interactive_message nodes
  transformSectionsForExport(node) {
    if (
      node.type === "interactiveMessage" &&
      node.data &&
      node.data.sections &&
      Array.isArray(node.data.sections)
    ) {
      // Check if sections are already in the new format (object with header and items)
      const isNewFormat =
        node.data.sections &&
        typeof node.data.sections === "object" &&
        !Array.isArray(node.data.sections) &&
        node.data.sections.header !== undefined &&
        node.data.sections.items !== undefined;

      if (!isNewFormat) {
        // Transform array to object with header and items
        const transformedSections = {
          header: node.data.listHeader || "",
          items: node.data.sections,
        };

        return {
          ...node,
          data: {
            ...node.data,
            sections: transformedSections,
          },
        };
      }
    }
    return node;
  }

  // Helper function to transform labels for import (reverse transformation)
  transformLabelsForImport(node) {
    if (
      (node.type === "interactiveMessage" || node.type === "questionMessage") &&
      node.data &&
      node.data.labels &&
      Array.isArray(node.data.labels) &&
      node.data.labels.length > 0
    ) {
      // Check if labels are in the new format (objects with label_id)
      const isNewFormat = node.data.labels.every(
        (label) => typeof label === "object" && label.label && label.label_id
      );

      if (isNewFormat) {
        // Transform object array back to string array
        const transformedLabels = node.data.labels.map((label) => label.label);

        return {
          ...node,
          data: {
            ...node.data,
            labels: transformedLabels,
          },
        };
      }
    }
    return node;
  }

  // Helper function to transform sections for import (reverse transformation)
  transformSectionsForImport(node) {
    if (
      node.type === "interactiveMessage" &&
      node.data &&
      node.data.sections &&
      typeof node.data.sections === "object" &&
      !Array.isArray(node.data.sections) &&
      node.data.sections.header !== undefined &&
      node.data.sections.items !== undefined
    ) {
      // Transform object back to array and extract header
      const transformedSections = node.data.sections.items || [];
      const listHeader = node.data.sections.header || "";

      return {
        ...node,
        data: {
          ...node.data,
          sections: transformedSections,
          listHeader: listHeader,
        },
      };
    }
    return node;
  }

  exportFlow() {
    const state = this.getState();
    console.log("=== EXPORTING FLOW ===", {
      nodeCount: state.nodes.length,
      nodes: state.nodes.map((n) => ({
        id: n.id,
        type: n.type,
        data: n.data,
        hasFilePath: n.type === "mediaMessage" ? !!n.data?.filePath : null,
        filePathValue: n.type === "mediaMessage" ? n.data?.filePath : null,
      })),
    });
    const exportData = {
      ...state,
      nodes: state.nodes.map((node) => {
        let transformedNode = this.transformLabelsForExport(node);
        transformedNode = this.transformSectionsForExport(transformedNode);
        return {
          ...transformedNode,
          isUpdated:
            transformedNode.isUpdated !== undefined
              ? transformedNode.isUpdated
              : false,
        };
      }),
    };
    return JSON.stringify(exportData);
  }

  importFlow(flowData) {
    try {
      console.log("=== IMPORTING FLOW ===", {
        flowDataLength: flowData.length,
      });

      const state = JSON.parse(flowData);
      console.log("=== PARSED IMPORT DATA ===", {
        hasNodes: !!state.nodes,
        hasEdges: !!state.edges,
        nodeCount: state.nodes?.length || 0,
        edgeCount: state.edges?.length || 0,
        nodes:
          state.nodes?.map((n) => ({
            id: n.id,
            type: n.type,
            hasData: !!n.data,
          })) || [],
      });
      if (!state.nodes || !Array.isArray(state.nodes)) {
        throw new Error("Invalid nodes data in import");
      }

      if (!state.edges || !Array.isArray(state.edges)) {
        console.warn(
          "No edges found in import, initializing empty edges array"
        );
        state.edges = [];
      }
      const validatedNodes = state.nodes.map((node) => {
        if (!node.id || !node.type) {
          throw new Error(
            `Invalid node: missing id or type - ${JSON.stringify(node)}`
          );
        }
        if (!node.data) {
          console.warn(`Node ${node.id} has no data, initializing empty data`);
          node.data = {};
        }

        // Apply import transformations to handle exported format
        let transformedNode = this.transformLabelsForImport(node);
        transformedNode = this.transformSectionsForImport(transformedNode);
        transformedNode = this.migrateLegacyVisualSearchNode(transformedNode);

        if (!this.validateNodeData(transformedNode.data, transformedNode.type)) {
          console.warn(
            `Node ${transformedNode.id} (${transformedNode.type}) has invalid data, using defaults`
          );
          transformedNode.data = this.getDefaultNodeData(transformedNode.type, transformedNode.data);
        }

        return transformedNode;
      });
      const validatedEdges = state.edges.map((edge) => {
        if (!edge.id || !edge.source || !edge.target) {
          throw new Error(
            `Invalid edge: missing required fields - ${JSON.stringify(edge)}`
          );
        }
        return {
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle || null,
          targetHandle: edge.targetHandle || null,
          type: edge.type || "default",
          animated: edge.animated || false,
          style: edge.style || {},
        };
      });

      const validatedState = {
        nodes: validatedNodes,
        edges: validatedEdges,
      };

      console.log("=== VALIDATED IMPORT DATA ===", {
        nodeCount: validatedState.nodes.length,
        edgeCount: validatedState.edges.length,
        nodes: validatedState.nodes.map((n) => ({ id: n.id, type: n.type })),
      });

      this.setState(validatedState);
      console.log("=== IMPORT COMPLETED SUCCESSFULLY ===");
    } catch (error) {
      console.error("=== IMPORT ERROR ===", error);
      throw new Error(`Failed to import flow: ${error.message}`);
    }
  }

  // Helper method to get default node data
  getDefaultNodeData(nodeType, existingData = {}) {
    switch (nodeType) {
      case "mediaMessage":
        return {
          mediaType: "image",
          caption: "",
          fileName: "",
          filePath: "",
          customFileName: "",
          selectedFile: null,
          previewUrl: "",
          mediaId: "",
          ...existingData,
        };
      case "normalMessage":
        return {
          text: "",
          ...existingData,
        };
      case "interactiveMessage":
        return {
          header: "Header",
          body: "Body",
          footer: "Powered by Orkaapps",
          labels: ["Label 1", "Label 2", "Label 3"],
          sections: [],
          listHeader: "",
          messageType: "buttons",
          answerVariable: "",
          ...existingData,
        };
      case "buttonNode":
        return {
          buttonLabel: "Click me",
          ...existingData,
        };
      case "custom":
        return {
          title: "",
          description: "",
          ...existingData,
        };
      case "labelChildNode":
        return {
          labels: ["Label 1", "Label 2", "Label 3"],
          ...existingData,
        };
      case "rowNode":
        return {
          title: "Row Title",
          description: "No description",
          sectionIndex: 0,
          rowIndex: 0,
          parentNodeId: null,
          edges: [],
          ...existingData,
        };
      case "catalogMessage":
        return {
          text: "",
          products: [],
          ...existingData,
        };
      case "templateMessage":
        return {
          templateId: null,
          variables: { header: {}, body: {}, footer: {} },
          ...existingData,
        };
      case "questionMessage":
        return {
          body: "",
          labels: [],
          answerVariable: "",
          answerFormat: "any",
          maxAttempts: 3,
          validationErrorMessage: "",
          regexPattern: "",
          abandonMessage: "",
          ...existingData,
        };
      case "flowMessage":
        return {
          body: "",
          buttonLabel: "Open Form",
          flowId: "",
          flowName: "",
          answerVariable: "",
          handlerType: "flow",
          ...existingData,
        };
      case "conditionNode":
        return {
          routerMode: "message",
          actionProfile: "customer_message",
          logic: "first_match",
          defaultLabel: "Default / fallback",
          branches: [
            {
              id: "has_reply",
              label: "Customer replied",
              description: "Customer sent any message.",
              match: "all",
              conditions: [
                {
                  source: "message",
                  field: "text",
                  operator: "exists",
                  value: "",
                },
              ],
            },
            {
              id: "media_uploaded",
              label: "Media uploaded",
              description: "Customer uploaded an image, document, audio, or video.",
              match: "all",
              conditions: [
                {
                  source: "message",
                  field: "has_media",
                  operator: "equals",
                  value: "true",
                },
              ],
            },
          ],
          conditions: [],
          ...existingData,
        };
      case "webhookMessage":
        return {
          method: "POST",
          url: "",
          headersEnabled: false,
          headers: [],
          bodyEnabled: false,
          requestBody: "",
          testEnabled: false,
          testVariables: [],
          testStatusCode: "",
          testResponseBody: "",
          saveResponseAsVariables: false,
          responseRoutingEnabled: false,
          expectedStatuses: ["200"],
          ...existingData,
        };
      case "paymentMessage":
        return {
          paymentType: "order_payment",
          header: "",
          body: "",
          footer: "",
          paymentItems: [{ name: "", amount: "" }],
          configurationId: "",
          configurationName: "",
          ...existingData,
        };
      default:
        return existingData;
    }
  }

  clearFlow() {
    this.setState({ nodes: [], edges: [] });
  }
}
const serviceId = appConfig.getServiceId();

class FlowService extends ApiService {
  constructor() {
    super();
    this.model = new FlowModel();
  }

  /**
   * Saves the entire flow, including nodes and edges.
   * Handles file uploads for MediaMessageNodes.
   * @param {object} flowData - The entire flow object { nodes, edges, viewport }.
   * @returns {Promise<object>} The saved flow data from the backend.
   */
  async saveFlow(flowData, chatbotId, templateId = null) {
    const state = this.model.getState();
    const { nodes, edges, viewport } = flowData;
    console.log("staeeeeeeee", state);

    console.log("nodes", nodes);

    // 1. Upload all media files and collect S3 URLs
    const mediaNodes = nodes.filter(
      (node) =>
        node.type === "mediaMessage" && node.data.selectedFile instanceof File
    );
    console.log("medianodes", mediaNodes);

    const uploadResults = await Promise.all(
      mediaNodes.map((node) =>
        this.uploadMediaNode(node.id, node.data.selectedFile)
      )
    );
    console.log("uploadResults", uploadResults);
    // uploadResults: [{ nodeId, s3Url }, ...]
    const resultMap = {};
    uploadResults.forEach((response, i) => {
      const result = response.data?.[0];
      const nodeId = mediaNodes[i].id;
      resultMap[nodeId] = {
        previewUrl: result.url,
        mediaId: result.id,
      };
    });
    console.log("resultMap", resultMap);

    // 2. Update nodes with S3 URLs and remove selectedFile
    const updatedNodes = nodes.map((node) => {
      if (node.type === "mediaMessage" && resultMap[node.id]) {
        const { selectedFile, ...restData } = node.data;
        return {
          ...node,
          data: {
            ...restData,
            previewUrl: resultMap[node.id].previewUrl,
            mediaId: resultMap[node.id].mediaId,
          },
        };
      } else if (node.type === "mediaMessage") {
        // Remove selectedFile if present
        const { selectedFile, ...restData } = node.data;
        return {
          ...node,
          data: {
            ...restData,
            previewUrl: node.data.previewUrl || "",
          },
        };
      } else {
        return node;
      }
    });

    // 3. Upload the full flow JSON
    // const finalFlowData = {
    //   nodes: updatedNodes,
    //   edges,
    //   viewport,
    // };
    const bodyData = {
      template:{
        nodes: updatedNodes,
        edges,
        viewport,
      },
      chatbot_id: chatbotId,
      id: templateId,
    }
    try {
      const { data } = await request.post(`/whatsapp/template/${appConfig.getServiceId()}`, bodyData);
      console.log("Flow saved successfully:", data);
      return data.template_json;
    } catch (error) {
      console.error("Error saving the entire flow:", error);
      throw error;
    }
  }

  async getFlow(chatbotId) {
    console.log('chatborId', chatbotId);
    
    const url = `/templates/${chatbotId}`;
    const { data } = await request.get(url);
    console.log('dattttttt', data);
    
    return data;
  }

  /**
   * Uploads a single media node's file and node id.
   * @param {string} nodeId - The id of the media message node.
   * @param {File} file - The file to upload.
   * @returns {Promise<object>} The updated node data from the backend (with S3 URL in previewUrl).
   */
  async uploadMediaNode(nodeId, file) {
    console.log("servicefile", file);
    console.log("nodeId", nodeId);

    const formData = new FormData();
    formData.append("files", file);
    formData.append("id", nodeId);
    formData.append("name", file.name);
    formData.append("type", file.type);
    formData.append("service_id", serviceId);
    console.log("formData", formData);

    try {
      const response = await request.post(
        `/upload-media/${appConfig.getServiceId()}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log(`Media for node ${nodeId} uploaded successfully:`, response);
      return response;
    } catch (error) {
      console.error(`Error uploading media for node ${nodeId}:`, error);
      throw error;
    }
  }
}

export default new FlowService();
